import { Room, RoomEvent, Track, type Participant, type RemoteTrack } from "livekit-client";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { SessionGrant } from "../api/types";

export type CallPhase = "idle" | "connecting" | "waiting_for_guide" | "live" | "ended" | "failed";

export interface BookingNotice {
  starts_at: string;
  timezone: string;
  duration_minutes: number;
}

export interface DemoRoom {
  phase: CallPhase;
  muted: boolean;
  guideSpeaking: boolean;
  /** True while the guide is sharing the product's screen; calls can be voice-only. */
  screenSharing: boolean;
  booking: BookingNotice | null;
  error: string | null;
  screenRef: RefObject<HTMLVideoElement | null>;
  audioRef: RefObject<HTMLDivElement | null>;
  connect: (grant: SessionGrant) => Promise<void>;
  toggleMute: () => Promise<void>;
  leave: () => Promise<void>;
  reset: () => void;
}

/** Messages the worker publishes on the room's data channel. Anything unrecognized is ignored. */
export function parseRoomMessage(payload: Uint8Array): { type: "booking_confirmed"; booking: BookingNotice } | null {
  try {
    const message: unknown = JSON.parse(new TextDecoder().decode(payload));
    if (typeof message !== "object" || message === null) {
      return null;
    }
    const { type, booking } = message as { type?: unknown; booking?: Partial<BookingNotice> };
    if (type === "booking_confirmed" && typeof booking?.starts_at === "string" && typeof booking.timezone === "string") {
      return {
        type,
        booking: {
          starts_at: booking.starts_at,
          timezone: booking.timezone,
          duration_minutes: Number(booking.duration_minutes) || 30,
        },
      };
    }
  } catch {
    // Not JSON — not one of ours.
  }
  return null;
}

export function useDemoRoom(): DemoRoom {
  const roomRef = useRef<Room | null>(null);
  const screenRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<CallPhase>("idle");
  const [muted, setMuted] = useState(false);
  const [guideSpeaking, setGuideSpeaking] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [booking, setBooking] = useState<BookingNotice | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async (grant: SessionGrant) => {
    await roomRef.current?.disconnect();
    const room = new Room({ adaptiveStream: true, dynacast: true });
    roomRef.current = room;
    setPhase("connecting");
    setError(null);
    setBooking(null);
    setMuted(false);
    setScreenSharing(false);

    room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Video && screenRef.current) {
          track.attach(screenRef.current);
          setScreenSharing(true);
        } else if (track.kind === Track.Kind.Audio) {
          audioRef.current?.append(track.attach());
        }
        setPhase((current) => (current === "waiting_for_guide" || current === "connecting" ? "live" : current));
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Video) {
          setScreenSharing(false);
        }
        for (const element of track.detach()) {
          if (element !== screenRef.current) {
            element.remove();
          }
        }
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) => {
        setGuideSpeaking(speakers.some((speaker) => speaker.identity !== room.localParticipant.identity));
      })
      .on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        const message = parseRoomMessage(payload);
        if (message) {
          setBooking(message.booking);
        }
      })
      .on(RoomEvent.Disconnected, () => {
        setGuideSpeaking(false);
        setPhase((current) => (current === "failed" ? current : "ended"));
      });

    try {
      await room.connect(grant.livekit_url, grant.token);
    } catch {
      setError("We couldn't connect to the demo. Check your network connection and try again.");
      setPhase("failed");
      return;
    }
    try {
      await room.localParticipant.setMicrophoneEnabled(true);
    } catch {
      setError(
        "The guide needs your microphone to hear you. Allow microphone access for this site, then start a new call.",
      );
      setPhase("failed");
      await room.disconnect();
      return;
    }
    setPhase(room.remoteParticipants.size > 0 ? "live" : "waiting_for_guide");
  }, []);

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) {
      return;
    }
    const enabled = room.localParticipant.isMicrophoneEnabled;
    await room.localParticipant.setMicrophoneEnabled(!enabled);
    setMuted(enabled);
  }, []);

  const leave = useCallback(async () => {
    await roomRef.current?.disconnect();
    setPhase("ended");
  }, []);

  const reset = useCallback(() => {
    roomRef.current = null;
    setPhase("idle");
    setBooking(null);
    setError(null);
    setGuideSpeaking(false);
    setScreenSharing(false);
  }, []);

  useEffect(() => {
    const rooms = roomRef;
    return () => {
      void rooms.current?.disconnect();
    };
  }, []);

  return {
    phase,
    muted,
    guideSpeaking,
    screenSharing,
    booking,
    error,
    screenRef,
    audioRef,
    connect,
    toggleMute,
    leave,
    reset,
  };
}
